/**
 * Canonical public MAATII demo event — same shape as production `EventData`.
 * Safe for anonymous users; never tied to a real Clerk user.
 */

import { eventCoverSrcThumbnail } from "@/lib/events/event-cover-display"
import type { EventData } from "@/lib/events/types"
import { parseIsoDateOnly } from "@/lib/events/event-datetime"
import { eventLocationPrimary } from "@/lib/location/display"
import { EVENT_VISIBILITY_PUBLIC } from "@/lib/events/visibility"
import {
  LANDING_IMAGES,
  landingMediaImageCrop,
} from "@/lib/marketing/landing-media"

export const PUBLIC_DEMO_EVENT_ID = "maatii_demo" as const

export type PublicDemoEventId = typeof PUBLIC_DEMO_EVENT_ID

export function isPublicDemoEventId(id: string): id is PublicDemoEventId {
  return id === PUBLIC_DEMO_EVENT_ID
}

const LOCALE = "en-US" as const

/** Props aligned with `EventCard` for the landing hero preview (links to `/demo`). */
export function getMarketingDemoEventCardProps() {
  const e = getPublicDemoEvent()
  const p = parseIsoDateOnly(e.date)
  const d = p ? new Date(p.y, p.m - 1, p.d) : null
  return {
    href: "/demo" as const,
    title: e.title,
    image: eventCoverSrcThumbnail(e),
    location: eventLocationPrimary(e),
    attendeesCount: e.attendees,
    attendeesCaption: "going" as const,
    dateHeader: {
      primary: d
        ? d.toLocaleDateString(LOCALE, { weekday: "long" })
        : "",
      secondary: d
        ? d.toLocaleDateString(LOCALE, { month: "long", day: "numeric" })
        : "",
    },
    dateLine: e.time
      ? `${e.time} · ${eventLocationPrimary(e)}`
      : eventLocationPrimary(e),
  }
}

/** Seeded row used in the client catalog and server read path. */
export function getPublicDemoEvent(): EventData {
  return {
    id: PUBLIC_DEMO_EVENT_ID,
    title: "Family gathering — Afternoon at home",
    date: "2026-05-31",
    time: "4:00 PM",
    location: "Parents' living room",
    locationName: "Family home",
    address: "",
    lat: undefined,
    lng: undefined,
    image: landingMediaImageCrop(LANDING_IMAGES.hero, 960, 480),
    imageThumbnailUrl: landingMediaImageCrop(LANDING_IMAGES.hero, 192, 192),
    hasPersistedShellCover: true,
    description: `We're hosting a slow afternoon in the living room — something warm on the table, soft light, and room for news, laughter, and the kind of talk that doesn't hurry.

Come as you are. If you can, let us know you're coming so we can set enough seats. Dietary notes are welcome when you RSVP — we'll make space.`,
    hostName: "Ami Okonkwo",
    hostAvatar:
      "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=96&h=96&q=68&crop=face",
    attendees: 18,
    isPublished: true,
    visibility: EVENT_VISIBILITY_PUBLIC,
    allowSignedInInviteesToViewAlbum: true,
    requireSignInToViewEventAlbum: false,
    canViewEventMedia: true,
    media: [],
    links: [],
    createdAt: new Date("2026-04-02T12:00:00.000Z"),
  }
}
